import React, { useCallback, useEffect, useState } from 'react';
import { AudioRecorderCore, type AudioRecorderStatus } from '../questions/AudioRecorderCore';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import './FollowUpJournal.css';

interface Props {
  id: string;
  value: string;
  disabled: boolean;
  describedBy?: string;
  /** Lined, borderless writing area for the journal page. */
  placeholder?: string;
  onChange: (value: string) => void;
  onBusyChange: (busy: boolean) => void;
}

/** Mount one composer per follow-up so a late recording cannot reach another turn. */
export const FollowUpReplyInput: React.FC<Props> = ({
  id, value, disabled, describedBy, placeholder = 'Keep writing…', onChange, onBusyChange,
}) => {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [status, setStatus] = useState<AudioRecorderStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recorderKey, setRecorderKey] = useState(0);
  const voiceBusy = status === 'recording' || status === 'transcribing';

  useEffect(() => {
    onBusyChange(voiceBusy);
    return () => onBusyChange(false);
  }, [onBusyChange, voiceBusy]);

  const handleStatus = useCallback((next: AudioRecorderStatus) => {
    setStatus(next);
    if (next === 'recording') setError(null);
  }, []);

  return <div className="space-y-2" data-testid="follow-up-composer">
    <div className="relative">
      <label htmlFor={id} className="sr-only">Your follow-up answer</label>
      <Textarea id={id} value={value} onChange={(event) => { setError(null); onChange(event.target.value); }}
        disabled={disabled || voiceBusy} rows={3} maxLength={16000}
        aria-describedby={describedBy}
        placeholder={placeholder}
        className="follow-up-journal-lined follow-up-journal-entry min-h-[5.6em] resize-none rounded-none border-0 px-0 py-0 pr-14 text-foreground shadow-none placeholder:italic placeholder:text-muted-foreground/70 rounded-md focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-4 focus-visible:ring-offset-transparent" />
      <fieldset className="absolute bottom-1 right-0 m-0 border-0 p-0" disabled={disabled && !voiceBusy}>
        <AudioRecorderCore key={recorderKey} audioBlob={audioBlob} onStatusChange={handleStatus}
          onError={setError}
          onRecordingComplete={(blob, transcription) => {
            // Failed/cancelled dictation must never erase an existing typed reply.
            if (!blob || !transcription?.trim()) return;
            setAudioBlob(blob);
            setError(null);
            onChange(value ? `${value}${/\s$/.test(value) ? '' : ' '}${transcription}` : transcription);
          }} />
      </fieldset>
    </div>
    {voiceBusy && <div className="flex items-center justify-between gap-3 text-sm">
      <p role="status" className="text-muted-foreground">{status === 'transcribing' ? 'Turning your voice into text…' : 'Listening…'}</p>
      <Button type="button" variant="ghost" size="auto" onClick={() => {
        setRecorderKey((key) => key + 1);
        setStatus('idle');
        setAudioBlob(null);
        setError(null);
      }}>Cancel recording</Button>
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {value.length > 16000 && <p role="alert" className="text-sm text-destructive">Please keep your reply to 16,000 characters or fewer.</p>}
  </div>;
};
